import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  ChartContainer
} from './styles';
import { toLocalDate } from '../../../utils/dateOnly';

const ROLE_COLORS = {
  Technical: '#3498db',    // Blue
  Functional: '#e74c3c',   // Red
  Organizational: '#2ecc71' // Green
};

const RoleHistoryModal = ({ isOpen, onClose, roleHistory, employeeName, onSubmit }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !roleHistory || !chartRef.current) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    // Get sorted roles for the y-axis
    const sortedRoles = [...(roleHistory.roles || [])].sort((a, b) => {
      // First sort by type
      const typeCompare = a.type.localeCompare(b.type);
      if (typeCompare !== 0) return typeCompare;
      // Then by order within type
      return a.order - b.order;
    });

    if (sortedRoles.length === 0) {
      console.error('No roles available in the response');
      return;
    }
    const roleLabels = sortedRoles.map(r => r.label);

    // Transform history data for the chart
    const historyData = (roleHistory.history || []).map(item => ({
      role: item.role.label,
      type: item.role.type,
      startDate: item.startDate,
      endDate: item.endDate || new Date().toISOString(),
      isOngoing: !item.endDate,
      updatedBy: item.updatedBy
    }));

    // Sort history by date
    const sortedHistory = [...historyData].sort((a, b) => 
      new Date(a.startDate) - new Date(b.startDate)
    );

    // Set up dimensions
    const margin = { top: 40, right: 40, bottom: 60, left: 250 };
    const containerWidth = chartRef.current.clientWidth;
    const containerHeight = chartRef.current.clientHeight;
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    // Create SVG with viewBox for better scaling
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`)
      .style('width', '100%')
      .style('height', '100%')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Calculate the offset based on the y-axis labels width
    const yAxisLabelWidth = 230;
    const labelPadding = 10;
    const chartStartX = yAxisLabelWidth + labelPadding;

    // Create scales
    const xScale = d3.scaleTime()
      .domain(d3.extent([
        ...sortedHistory.map(d => new Date(d.startDate)),
        ...sortedHistory.map(d => new Date(d.endDate))
      ]))
      .range([0, width])
      .nice();

    const yScale = d3.scalePoint()
      .domain(roleLabels)
      .range([height, 0])
      .padding(0.3);

    // Add axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .ticks(10)
        .tickFormat(d3.timeFormat('%b %d, %Y')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    // Add y-axis with colored labels
    const yAxis = svg.append('g')
      .call(d3.axisLeft(yScale));

    // Remove the default axis line
    yAxis.select('.domain').remove();
    // Remove the default tick lines
    yAxis.selectAll('.tick line').remove();

    // Style the y-axis labels
    yAxis.selectAll('.tick')
      .append('rect')
      .attr('x', -230)
      .attr('y', -15)
      .attr('width', 220)
      .attr('height', 30)
      .attr('rx', 4)
      .style('fill', d => {
        const role = sortedRoles.find(r => r.label === d);
        return `${ROLE_COLORS[role.type]}10`;
      })
      .style('stroke', d => {
        const role = sortedRoles.find(r => r.label === d);
        return ROLE_COLORS[role.type];
      })
      .style('stroke-width', 1);

    // Add dots before text
    yAxis.selectAll('.tick')
      .append('circle')
      .attr('cx', -200)
      .attr('cy', 0)
      .attr('r', 4)
      .style('fill', d => {
        const role = sortedRoles.find(r => r.label === d);
        return ROLE_COLORS[role.type];
      });

    // Style the text labels
    yAxis.selectAll('.tick text')
      .attr('x', -185)
      .attr('dy', '.32em')
      .style('font-size', '12px')
      .style('fill', '#333')
      .style('font-weight', '500')
      .style('text-anchor', 'start');

    // Add grid lines
    svg.selectAll('line.horizontal-grid')
      .data(roleLabels)
      .enter()
      .append('line')
      .attr('class', 'horizontal-grid')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .style('stroke', d => {
        const role = sortedRoles.find(r => r.label === d);
        return `${ROLE_COLORS[role.type]}30`;
      })
      .style('stroke-width', 1);

    // Add role timeline bars
    svg.selectAll('.role-bar')
      .data(sortedHistory)
      .enter()
      .append('rect')
      .attr('class', 'role-bar')
      .attr('x', d => xScale(new Date(d.startDate)))
      .attr('y', d => yScale(d.role) - 10)
      .attr('width', d => xScale(new Date(d.endDate)) - xScale(new Date(d.startDate)))
      .attr('height', 20)
      .attr('rx', 4)
      .style('fill', d => `${ROLE_COLORS[d.type]}50`)
      .style('stroke', d => ROLE_COLORS[d.type])
      .style('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        const [mouseX, mouseY] = d3.pointer(event, chartRef.current);
        
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(
          `<div style="color: ${ROLE_COLORS[d.type]}">
            <strong>${d.role}</strong> (${d.type})
           </div>
           ${toLocalDate(d.startDate)?.toLocaleDateString()} - ${d.isOngoing ? '<strong style="color: #22c55e">ongoing</strong>' : toLocalDate(d.endDate)?.toLocaleDateString()}<br/>
           Updated by: ${d.updatedBy}`
        )
          .style('left', `${mouseX + 10}px`)
          .style('top', `${mouseY - 28}px`);
      })
      .on('mouseout', () => {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });

    // Create tooltip
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('z-index', 1000);
  }, [isOpen, roleHistory]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <span>{employeeName}</span>
          </Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ChartContainer ref={chartRef} />
      </ModalContent>
    </ModalOverlay>
  );
};

export default RoleHistoryModal; 